import { type ElementType, type ComponentPropsWithoutRef, type HTMLAttributes, type Ref, type ComponentRef } from 'react';
import { cn } from '#utils';

type PolymorphicCardProps<E extends ElementType = 'div'> = {
  /**
   * The element type to render as
   * @default "div"
   * @example "article" | "section" | "button"
   */
  as?: E;
  /**
   * Border radius variant
   * @default "default" (rounded-lg)
   */
  rounded?: "default" | "lg" | "xl" | "2xl" | "3xl" | "full" | "none";
  /**
   * Whether to show border
   * @default true
   */
  bordered?: boolean;
  /**
   * Whether to show shadow
   * @default true
   */
  shadow?: boolean;
} & ComponentPropsWithoutRef<E>;

type CardProps<E extends ElementType = 'div'> = PolymorphicCardProps<E>;

function Card<E extends ElementType = 'div'>({
  as,
  className,
  rounded = "default",
  bordered = true,
  shadow = true,
  ref,
  ...props
}: CardProps<E> & { ref?: Ref<ComponentRef<E>> }) {
    const Component = (as || 'div') as ElementType;
    
    const roundedClasses = {
      none: "",
      default: "rounded-lg",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
      "3xl": "rounded-3xl",
      full: "rounded-full",
    };

    return (
      <Component
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
        className={cn(
          "bg-card text-card-foreground",
          roundedClasses[rounded],
          bordered && "border border-border",
          shadow && "shadow-sm",
          className
        )}
        {...props}
      />
    );
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Spacing variant
   * @default "default"
   */
  spacing?: "none" | "sm" | "default" | "lg";
}

function CardHeader({
  className,
  spacing = "default",
  ref,
  ...props
}: CardHeaderProps & { ref?: Ref<HTMLDivElement> }) {
    const spacingClasses = {
      none: "",
      sm: "p-4",
      default: "p-6",
      lg: "p-8",
    };

    return (
      <div
        ref={ref}
        className={cn("flex flex-col space-y-1.5", spacingClasses[spacing], className)}
        {...props}
      />
    );
}

function CardTitle({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { ref?: Ref<HTMLHeadingElement> }) {
  return (
    <h3
      ref={ref}
      className={cn("text-2xl font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
}

function CardDescription({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { ref?: Ref<HTMLParagraphElement> }) {
  return (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )
}

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Spacing variant
   * @default "default"
   */
  spacing?: "none" | "sm" | "default" | "lg";
}

function CardContent({
  className,
  spacing = "default",
  ref,
  ...props
}: CardContentProps & { ref?: Ref<HTMLDivElement> }) {
    const spacingClasses = {
      none: "",
      sm: "p-4",
      default: "p-6 pt-0",
      lg: "p-8 pt-0",
    };

    return (
      <div
        ref={ref}
        className={cn(spacingClasses[spacing], className)}
        {...props}
      />
    );
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Spacing variant
   * @default "default"
   */
  spacing?: "none" | "sm" | "default" | "lg";
}

function CardFooter({
  className,
  spacing = "default",
  ref,
  ...props
}: CardFooterProps & { ref?: Ref<HTMLDivElement> }) {
    const spacingClasses = {
      none: "",
      sm: "p-4 pt-0",
      default: "p-6 pt-0",
      lg: "p-8 pt-0",
    };

    return (
      <div
        ref={ref}
        className={cn("flex items-center", spacingClasses[spacing], className)}
        {...props}
      />
    );
}

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
export type { CardProps, CardHeaderProps, CardContentProps, CardFooterProps };
